import React from "react";

import { useMapsLibrary } from "@/finder/googleMaps/GoogleAPIProvider";
import { useMapContext } from "@/finder/googleMaps/GoogleMapsProvider";
import { useFinder } from "@/finder/hooks/V2/useFinder";

// Handles the logic for the map view, including setting the bounds and zoom level, and panning to a selected location
// This component is NOT a wrapper around the Google Maps API, but rather a custom controller built specifically for the Finder
const MapViewController = ({ hidden }: { hidden: boolean }) => {
  const { map, defaultZoom } = useMapContext();
  const singleLocationZoomLevel = 14;
  const [lastZoomLevel, setLastZoomLevel] = React.useState<number>(defaultZoom);
  if (!map) {
    throw new Error("MapViewController must be used within a Map component");
  }
  const mapLibrary = useMapsLibrary();
  const { place, hasVisibleExpandedResults, locationSelected, visibleSearchResults } = useFinder();
  const { hasPanned } = useMapContext();

  // Apply offset to the map to center it in the viewport and account for the width of the sidebar
  // This is only necessary for desktop view, as the map is already centered on mobile
  // It is called after the bounds are set to ensure that the map is centered correctly
  const applyOffset = React.useCallback(() => {
    const viewportColumn = document.querySelector("#view-column");
    if (!viewportColumn) {
      return;
    }
    const panOffsetX = viewportColumn.clientWidth / 2;
    map.panBy(-panOffsetX, 0);
  }, [map]);

  const mapBounds = React.useMemo(() => {
    if (hasPanned) {
      return new google.maps.LatLngBounds();
    }

    const bounds = new google.maps.LatLngBounds();

    if (!!visibleSearchResults?.length) {
      visibleSearchResults.forEach((result) => {
        bounds.extend({
          lat: result.location.position.lat,
          lng: result.location.position.lon
        });
      });

      if (place) {
        bounds.extend({
          lat: place.latitude,
          lng: place.longitude
        });
      }

      // TLDR of the next several lines: make bounds square.
      // Square bounds ensures that the map is zoomed out enough to show all search results.
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      const latCenter = (ne.lat() + sw.lat()) / 2;
      const lngCenter = (ne.lng() + sw.lng()) / 2;

      // find the longest edge of the rectangle
      let latDiff = ne.lat() - sw.lat();
      let lngDiff = (ne.lng() - sw.lng()) * Math.cos(latCenter * (Math.PI / 180)); // Correct for longitude distortion
      const maxDiff = Math.max(latDiff, lngDiff);

      latDiff = maxDiff;
      lngDiff = maxDiff / Math.cos(latCenter * (Math.PI / 180));

      const squareSw = new google.maps.LatLng(latCenter - latDiff / 2, lngCenter - lngDiff / 2);
      const squareNe = new google.maps.LatLng(latCenter + latDiff / 2, lngCenter + lngDiff / 2);

      return new google.maps.LatLngBounds(squareSw, squareNe);
    }

    return bounds;
  }, [place, visibleSearchResults, hasPanned]);

  // Set the bounds of the map to fit all search results --> Don't need to do this on render's where the map is hidden.
  React.useEffect(() => {
    if (!mapLibrary || hidden || !map || mapBounds.isEmpty()) {
      return;
    }

    if (!!visibleSearchResults?.length) {
      map.fitBounds(mapBounds, { top: 50, left: 8, right: 8, bottom: 50 });
      setLastZoomLevel((prevZoom) => map.getZoom() || prevZoom);
      applyOffset();
    } else if (place) {
      map.setCenter({
        lat: place.latitude,
        lng: place.longitude
      });
      map.setZoom(singleLocationZoomLevel - 1);
      setLastZoomLevel(singleLocationZoomLevel - 1);
    }
  }, [
    applyOffset,
    hidden,
    map,
    mapBounds,
    mapLibrary,
    place,
    hasVisibleExpandedResults,
    visibleSearchResults
  ]);

  // Define behavior for when a location is selected
  React.useEffect(() => {
    if (!!map && !!locationSelected) {
      map.panTo({
        lat: locationSelected.location.position.lat,
        lng: locationSelected.location.position.lon
      });
      map.setZoom(lastZoomLevel);
      applyOffset();
    }
  }, [applyOffset, lastZoomLevel, locationSelected, map]);
  return null;
};

export default React.memo(MapViewController);
